import { useQuery, queryOptions, keepPreviousData } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { fetchContactBlacklist } from '../api/contactBlacklistApi';
import {
	fetchAllContactLists,
	fetchContactListDetail,
	fetchListContactsPage,
	fetchContactsByListIds,
	fetchListCompaniesPage,
	fetchCompaniesByListIds,
	fetchCampaignRecipients,
	searchContactLists,
} from '../api/contactListsApi';
import { BLACKLIST_PAGE_SIZE, LIST_RECIPIENTS_PAGE_SIZE } from '../constants/tableFilters';

import { contactBlacklistKeys, contactListsKeys } from './keys';

export const contactListDetailOptions = (listId) =>
	queryOptions({
		queryKey: contactListsKeys.detail(listId),
		queryFn: () => fetchContactListDetail(listId),
		staleTime: STALE_TIMES.STANDARD,
		enabled: Boolean(listId),
	});

export function useContactListDetail(listId) {
	return useQuery(contactListDetailOptions(listId));
}

export const contactListContactsOptions = (listId, { page, filters }) =>
	queryOptions({
		queryKey: contactListsKeys.contacts(listId, { page, filters }),
		queryFn: () => fetchListContactsPage(listId, { page, pageSize: LIST_RECIPIENTS_PAGE_SIZE, filters }),
		staleTime: STALE_TIMES.FREQUENT,
		enabled: Boolean(listId),
		placeholderData: keepPreviousData,
	});

export function useContactListContacts(listId, params) {
	return useQuery(contactListContactsOptions(listId, params));
}

export const contactListCompaniesOptions = (listId, { page, filters }) =>
	queryOptions({
		queryKey: contactListsKeys.companies(listId, { page, filters }),
		queryFn: () => fetchListCompaniesPage(listId, { page, pageSize: LIST_RECIPIENTS_PAGE_SIZE, filters }),
		staleTime: STALE_TIMES.FREQUENT,
		enabled: Boolean(listId),
		placeholderData: keepPreviousData,
	});

export function useContactListCompanies(listId, params) {
	return useQuery(contactListCompaniesOptions(listId, params));
}

export const contactListsOptions = () =>
	queryOptions({
		queryKey: contactListsKeys.list(),
		queryFn: async () => {
			const { data, error } = await fetchAllContactLists();
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.STANDARD,
		retry: 2,
	});

export function useContactLists() {
	return useQuery(contactListsOptions());
}

export const contactsByListIdsOptions = (listIds) =>
	queryOptions({
		queryKey: contactListsKeys.contactsByListIds(listIds),
		queryFn: async () => {
			const { data, error } = await fetchContactsByListIds(listIds);
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.STANDARD,
	});

export function useContactsByListIds(listIds) {
	return useQuery({
		...contactsByListIdsOptions(listIds),
		enabled: Boolean(listIds) && listIds.length > 0,
	});
}

export const companiesByListIdsOptions = (listIds) =>
	queryOptions({
		queryKey: contactListsKeys.companiesByListIds(listIds),
		queryFn: async () => {
			const { data, error } = await fetchCompaniesByListIds(listIds);
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.STANDARD,
	});

export function useCompaniesByListIds(listIds) {
	return useQuery({
		...companiesByListIdsOptions(listIds),
		enabled: Boolean(listIds) && listIds.length > 0,
	});
}

export function useCampaignRecipients(campaignId) {
	return useQuery({
		queryKey: contactListsKeys.campaignRecipients(campaignId),
		queryFn: () => fetchCampaignRecipients(campaignId),
		enabled: Boolean(campaignId),
		staleTime: STALE_TIMES.STANDARD,
	});
}

export const contactBlacklistOptions = ({ page, filters }) =>
	queryOptions({
		queryKey: contactBlacklistKeys.list({ page, filters }),
		queryFn: () =>
			fetchContactBlacklist({ offset: (page - 1) * BLACKLIST_PAGE_SIZE, limit: BLACKLIST_PAGE_SIZE, filters }),
		staleTime: STALE_TIMES.STANDARD,
		placeholderData: keepPreviousData,
	});

export function useContactBlacklist(params) {
	return useQuery(contactBlacklistOptions(params));
}

/**
 * @param {string} q
 * @param {{listIds?: string[]}} [options] - `listIds` restricts the search to these lists
 */
export function useContactListSearch(q, { listIds } = {}) {
	return useQuery({
		queryKey: contactListsKeys.search(q, listIds),
		queryFn: () => searchContactLists({ q, listIds }),
		enabled: q.trim().length >= 2,
		staleTime: STALE_TIMES.FREQUENT,
		placeholderData: keepPreviousData,
	});
}
